import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Banknote, Palette, Zap, Leaf, ChevronDown } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { ServiceSchema, BreadcrumbSchema, FAQPageSchema } from '@/components/SchemaOrg';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  images: string[] | null;
  status: string;
  categories: string[] | null;
}

const StampaDtf = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, slug, price, original_price, images, status, categories')
        .contains('categories', ['inchiostri'])
        .eq('status', 'disponibile')
        .limit(4);

      if (!error && data) {
        setProducts(data);
      }
      setLoading(false);
    };

    fetchProducts();
  }, []);

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || '/placeholder.svg',
    });
    toast.success(`${product.name} aggiunto al carrello`);
  };

  const features = [
    {
      icon: <Banknote className="w-8 h-8" />,
      title: 'Opzioni Convenienti',
      description: 'Inchiostro pigmentato a base acqua con grande scorrevolezza, elevata velocità di sviluppo del colore e meno dosaggio.'
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: 'Colori Brillanti',
      description: 'Colori vivaci con tecnologia di gestione del colore ICC. Riproduzione dell\'immagine elevata e cromatogramma completo.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Alta Durata',
      description: 'Il colore rimane brillante dopo 40 lavaggi. Solidità del colore di livello 4, non facile da sbiadire.'
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Eco-Friendly',
      description: 'Standard REACH, SVHC 211, RoHS. Ingredienti sicuri senza sostanze pericolose per la pelle.'
    }
  ];

  const colors = [
    { name: 'Ciano', color: 'bg-cyan-500' },
    { name: 'Magenta', color: 'bg-pink-500' },
    { name: 'Giallo', color: 'bg-yellow-400' },
    { name: 'Nero', color: 'bg-black' },
    { name: 'Bianco', color: 'bg-white border border-gray-300' }
  ];

  const faqs = [
    {
      question: "Cos'è l'inchiostro DTF?",
      answer: "L'inchiostro DTF è un inchiostro pigmentato specializzato utilizzato nella stampa Direct-To-Film, progettato per essere stampato direttamente su pellicola di trasferimento."
    },
    {
      question: "Di cosa è fatto l'inchiostro DTF?",
      answer: "L'inchiostro DTF è composto da pigmenti per il colore, leganti per l'adesione, additivi per proprietà specifiche e solventi per la viscosità ottimale."
    },
    {
      question: "Quali stampanti sono compatibili?",
      answer: "I nostri inchiostri DTF sono compatibili con le principali testine di stampa del mercato, incluse Epson 4720, i3200, DX5, DX7 e molte altre."
    }
  ];

  return (
    <>
      <SEOHead
        title="Inchiostro DTF Premium | Stampa DTF Professionale | Materialidtf.it"
        description="Inchiostro DTF di alta qualità per stampanti DTF. Colori vivaci, alta durata dopo 40 lavaggi, eco-friendly. Compatibile con Epson 4720, i3200 e altre testine."
        canonical="/stampa-dtf"
        keywords="inchiostro DTF, stampa DTF, inchiostro pigmentato DTF, colori DTF, stampa tessuti"
      />
      <ServiceSchema
        name="Inchiostro DTF Premium"
        description="Inchiostro DTF professionale per stampa tessile di alta qualità. Colori brillanti, durata garantita e formula eco-friendly."
        url="/stampa-dtf"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Stampa DTF', url: '/stampa-dtf' }
        ]}
      />
      <FAQPageSchema items={faqs} />

      <MainHeader />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="bg-primary py-16 px-4">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
              Inchiostro DTF
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-3xl mx-auto">
              Per consentire alla tua stampante DTF di funzionare senza problemi, è fondamentale scegliere un inchiostro pigmentato DTF compatibile. Ottieni qualità professionale senza intasamenti e altri problemi.
            </p>
          </div>
        </section>

        {/* Product Spotlight */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img 
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/694415ba14157442c13a5ac2/06c77d036_image.png"
                  alt="Inchiostro DTF Premium - bottiglie colorate per stampa tessile professionale" 
                  className="rounded-xl shadow-2xl w-full"
                  loading="lazy"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Inchiostro DTF Premium
                </h2>
                <p className="text-muted-foreground mb-8">
                  La formula speciale per colori eccezionali e puliti funziona con elevata flessibilità ed elasticità per un vasto assortimento di testine di stampa di marca sul mercato.
                </p>
                
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">Quantità</p>
                    <p className="text-lg font-bold text-foreground">1000ml/bottiglia</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">Colori</p>
                    <p className="text-lg font-bold text-foreground">K C M Y WH</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">Temperatura</p>
                    <p className="text-lg font-bold text-foreground">130-160°C</p>
                  </div>
                </div>

                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Richiedi un preventivo
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Color Range */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">
              Colori Vibranti
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Ampia gamma di colori, gocce di grandi dimensioni, e consistenza molecolare per formare una grafica a colori precisa come ciano, magenta, giallo, nero, e bianco.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {colors.map((color, index) => (
                <div key={index} className="flex flex-col items-center gap-2">
                  <div className={`w-16 h-16 rounded-full ${color.color} shadow-lg`} />
                  <span className="text-sm font-medium text-foreground">{color.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Caratteristiche Principali
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-muted/30 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-primary mb-4 flex justify-center">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-16 px-4 bg-primary">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-primary-foreground text-center mb-12">
              I Nostri Prodotti DTF
            </h2>
            
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-xl" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-lg group">
                    <div className="relative aspect-square overflow-hidden">
                      {product.original_price && product.original_price > product.price && (
                        <span className="absolute top-2 left-2 bg-destructive text-white text-xs font-bold px-2 py-1 rounded z-10">
                          OFFERTA
                        </span>
                      )}
                      <Link to={`/prodotto/${product.slug}`}>
                        <img 
                          src={product.images?.[0] || '/placeholder.svg'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    </div>
                    <div className="p-4">
                      <Link to={`/prodotto/${product.slug}`}>
                        <h3 className="font-bold text-foreground mb-2 hover:text-primary transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 mb-4">
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-muted-foreground line-through text-sm">
                            €{product.original_price.toFixed(2)}
                          </span>
                        )}
                        <span className="text-lg font-bold text-primary">
                          €{product.price.toFixed(2)}
                        </span>
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={() => handleAddToCart(product)}
                      >
                        Aggiungi al carrello
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-primary-foreground/80 text-center">Nessun prodotto disponibile al momento.</p>
            )}

            <div className="text-center mt-8">
              <Button asChild variant="secondary" size="lg">
                <Link to="/categoria/inchiostri">Vedi tutti i prodotti</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Domande Frequenti
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-muted/30 rounded-xl px-6 border-none"
                >
                  <AccordionTrigger className="text-foreground font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">
              Pronto a Migliorare le Tue Stampe?
            </h2>
            <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Contattaci per una consulenza gratuita sui nostri inchiostri DTF e scopri la qualità professionale
            </p>
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Richiedi una Consulenza Gratuita
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default StampaDtf;
